import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { WARRANTY_VENDOR_POLICIES_URL } from "../../Shared/constant";
import { getVendorPolicy } from "../common/warrantyApi";
import { formatDateTime, safeHttpUrl } from "../common/format";
import "../warranty.css";

// The vendor policy is bought for the theft and roadside covers of Assure+ only.
const VENDOR_COVER_KINDS = ["theft", "roadside"];

export const hasTheftOrRoadside = (entitlements) =>
  Array.isArray(entitlements) && entitlements.some((item) => VENDOR_COVER_KINDS.includes(item?.kind));

const httpsUrl = (value) => {
  const url = safeHttpUrl(value);
  return url && url.startsWith("https:") ? url : null;
};

// The vendor policy of one subscription, on its detail screen: the policy number and when it was
// added, with View policy, or a pointer to Theft & roadside to buy when none was added yet. The PDF
// link lasts 15 minutes, so View policy asks for a fresh one on every click.
const VendorPolicyFacts = ({ accessToken, subscriptionId }) => {
  const [view, setView] = useState({ status: "loading", data: null, message: "" });
  const [opening, setOpening] = useState({ busy: false, message: "" });
  const mounted = useRef(true);
  const seq = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setView({ status: "loading", data: null, message: "" });
    const result = await getVendorPolicy(accessToken, subscriptionId);
    if (!mounted.current || mine !== seq.current) return;
    if (result.ok) {
      setView({ status: "ready", data: result.data, message: "" });
    } else if (result.notFound) {
      setView({ status: "none", data: null, message: "" });
    } else {
      setView({ status: "error", data: null, message: result.message });
    }
  }, [accessToken, subscriptionId]);

  useEffect(() => {
    load();
  }, [load]);

  // The tab is opened inside the click (a popup blocker allows only that) and pointed at the link
  // when it arrives.
  const openPolicy = async () => {
    if (opening.busy) return;
    const tab = window.open("about:blank", "_blank");
    if (!tab) {
      setOpening({ busy: false, message: "Your browser blocked the new tab. Allow pop-ups for this site and try again." });
      return;
    }
    tab.opener = null;
    setOpening({ busy: true, message: "" });
    const result = await getVendorPolicy(accessToken, subscriptionId);
    if (!mounted.current) {
      tab.close();
      return;
    }
    const url = result.ok ? httpsUrl(result.data?.url) : null;
    if (url) {
      tab.location.href = url;
      setOpening({ busy: false, message: "" });
    } else {
      tab.close();
      setOpening({ busy: false, message: result.ok ? "The policy link is not usable." : result.message });
    }
  };

  if (view.status === "loading") {
    return <div className="warranty-muted">Loading the vendor policy</div>;
  }
  if (view.status === "error") {
    return (
      <div className="warranty-field-msg is-error" role="alert">
        {view.message}{" "}
        <button type="button" className="warranty-inline-btn" onClick={load}>
          Try again
        </button>
      </div>
    );
  }
  if (view.status === "none") {
    return (
      <div className="warranty-muted">
        Not added yet. Add it from <Link to={WARRANTY_VENDOR_POLICIES_URL}>Theft &amp; roadside to buy</Link>.
      </div>
    );
  }
  return (
    <>
      <dl className="warranty-facts">
        <dt>Policy number</dt>
        <dd>{view.data?.policyNumber || "-"}</dd>
        <dt>Added</dt>
        <dd>{formatDateTime(view.data?.issuedAt)}</dd>
      </dl>
      <div className="warranty-more">
        {opening.message && (
          <span className="warranty-field-msg is-error" role="alert">
            {opening.message}
          </span>
        )}
        <Button className="Addbtn" onClick={openPolicy} disabled={opening.busy}>
          {opening.busy ? "Opening" : "View policy"}
        </Button>
      </div>
    </>
  );
};

export default VendorPolicyFacts;
